import React, { useCallback, useState } from 'react'
import { PiWarningCircle } from 'react-icons/pi'
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Spinner,
  Typography,
} from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { useWindowSize } from '../hooks/toolkit'
import { useGeneralToast } from '../hooks/toast'
import { deleteExperience } from '../utils/helpers/supabase.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'

interface RemoveExperienceDialogProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  experienceName: string
  experienceId: string
  refetchExpList: () => void
}

const RemoveExperienceDialog: React.FC<RemoveExperienceDialogProps> = ({
  isOpen,
  setIsOpen,
  experienceName,
  experienceId,
  refetchExpList,
}) => {
  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const [deleting, setDeleting] = useState(false)
  const { width } = useWindowSize()
  const dialogSize = (width ?? 0) <= 640 ? 'lg' : 'xs'
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const deleteExperienceFromLibrary = useCallback(async () => {
    setDeleting(true)
    const { error } = await deleteExperience({ experienceId })

    if (error) {
      toastError(t('removeExperience.error'))
      setDeleting(false)
      return
    }

    await refetchExpList()
    toastSuccess(t('removeExperience.success'))
    setDeleting(false)
    setIsOpen(false)
  }, [experienceId, toastError, toastSuccess, refetchExpList, setIsOpen, t])

  return (
    <Dialog size={dialogSize} open={isOpen} handler={setIsOpen} data-testid="delete-experience-dialog">
      <DialogHeader>{t('removeExperience.title')}</DialogHeader>
      <DialogBody>
        <Alert
          variant={darkMode ? 'outlined' : 'ghost'}
          color="orange"
          icon={<PiWarningCircle className="h-6 w-6" />}
        >
          {t('removeExperience.warning')}
        </Alert>

        <Typography className="block my-2">
          {t('removeExperience.confirmMessage')} <strong className="font-extrabold">{experienceName}</strong>{' '}
          {t('removeExperience.fromLibrary')}
        </Typography>
      </DialogBody>
      <DialogFooter className="justify-between gap-2">
        <Button
          size="sm"
          color={darkMode ? 'white' : 'blue-gray'}
          variant="outlined"
          onClick={() => setIsOpen(false)}
          disabled={deleting}
        >
          {t('removeExperience.cancel')}
        </Button>
        <Button
          size="sm"
          color="red"
          onClick={deleteExperienceFromLibrary}
          disabled={deleting}
          data-testid="delete-experience-confirm-btn"
        >
          {deleting ? <Spinner className="h-4 w-4" /> : t('removeExperience.confirmDelete')}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export default RemoveExperienceDialog
